import React, { useEffect, useMemo, useState } from "react";
import { ReportSummaryItemApi } from "../../api/Reports";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import ViewReportLogsButton from "../../shared-components/ViewReportLogsButton";
import EuRegionBadge from "../../shared-components/EuRegionBadge";
import { PersonInfoList } from "./SidebarPersonInfo";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { classNames, LONG_TEXT_WRAP_CLASSES } from "../../utils";
import LabeledCheckbox from "../../shared-components/LabeledCheckbox";
import { SidebarBehaviors } from "./SidebarBehaviors";
import { SidebarKeywords } from "./SidebarKeywords";
import InputField from "../../shared-components/InputField";
import { ReportDetailTypes } from "../utils";
import Button, { ButtonColorTypes } from "../../shared-components/Button";
import useNavigateNewPerson from "../useNavigateNewPerson";
import { useReportCancel } from "../../api/ReportsApi";
import {
  ConfirmDialogActions,
  useConfirmationDialog,
} from "../../shared-components/ConfirmationDialog";
import { useReportDetail } from "../ReportDetailProvider";
import { SummaryFilterTypeEnum } from "./SidebarSummary";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { SidebarTopics } from "./SidebarTopics";
import { useKeywordGroupsForBehaviorKit } from "../../api/BlueprintsApi";
import { countByName, displayedCount, FilterCount } from "./sidebarFilterCount";
import {
  useListedCount,
  useUnfilteredCountBaseline,
} from "./useUnfilteredCountBaseline";

const allowedItemTypes: { [key: string]: string } = {
  social_media_post: "Posts",
  web_content: "Articles",
};

const SourcesSkiped = ["PDFGenerator"];

type Props = {
  report: ReportSummaryItemApi;
  showReportLog: () => void;
  errorBlueprint: boolean;
  checkTypeColor: string;
  hideItemTypeFilter?: boolean;
  applyFilterByType: (
    type: SummaryFilterTypeEnum,
    event: React.ChangeEvent<HTMLInputElement> | null,
    source: string
  ) => void;
  reportDetailType: ReportDetailTypes;
  subStep: string;
  isFilterEmpty: boolean;
  dateFilter: { fromDate: string; toDate: string };
};

const SidebarAuditQAPosts = ({
  report,
  showReportLog,
  applyFilterByType,
  errorBlueprint,
  checkTypeColor,
  hideItemTypeFilter = false,
  reportDetailType,
  subStep,
  isFilterEmpty,
  dateFilter,
}: Props) => {
  const [showSourceFilter, setShowSourceFilter] = useState(true);
  const [showBehaviorsFilter, setShowBehaviorsFilter] = useState(true);
  const [showKeywordFilter, setShowKeywordFilter] = useState(true);
  const [showTopicFilter, setShowTopicFilter] = useState(true);
  const [showPostTypeFilter, setShowPostTypeFilter] = useState(true);
  const [showItemTypeFilter, setShowItemTypeFilter] = useState(true);
  const [showDateFilter, setShowDateFilter] = useState(true);
  const [selections, setSelections] = useState<{
    [key: string]: string[];
  }>({
    behavior: [],
    keyword: [],
    topic: [],
    postType: [],
    itemType: [],
    source: [],
  });
  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const {
    baselineFlagCount,
    baselineKeywordCount,
    baselineTopicCount,
    baselineLoading,
    baselineError,
  } = useUnfilteredCountBaseline(report.report_uuid);

  const { canAddNotes } = usePermissionValidator();

  const {
    flagCount,
    keywordCount,
    topicCount,
    flagsLoading,
    filteredCountsReady,
  } = useReportDetail();
  const { data: keywordGroups } = useKeywordGroupsForBehaviorKit(
    report.behavior_kit_uuid
  );

  useEffect(() => {
    setFromDate(dateFilter.fromDate || "");
    setToDate(dateFilter.toDate || "");
  }, [dateFilter]);

  const { modal, hide, show } = useConfirmationDialog();
  const { mutate: reportCancel } = useReportCancel();
  const { navigateCreatePerson } = useNavigateNewPerson();

  const listedFlagCount = useListedCount(
    baselineFlagCount,
    baselineError,
    flagCount,
    isFilterEmpty
  );
  const listedKeywordCount = useListedCount(
    baselineKeywordCount,
    baselineError,
    keywordCount,
    isFilterEmpty
  );
  const listedTopicCount = useListedCount(
    baselineTopicCount,
    baselineError,
    topicCount,
    isFilterEmpty
  );
  const showFlagTotals =
    !isFilterEmpty && filteredCountsReady !== false && !!baselineFlagCount;
  const itemTypesOptions = useMemo(
    () =>
      (listedFlagCount?.item_types?.groups ?? [])
        .filter((itemType) => itemType.name in allowedItemTypes)
        .map((itemType) => allowedItemTypes[itemType.name])
        .sort(),
    [listedFlagCount]
  );
  const itemSourcesOptions = useMemo(() => {
    const names = (listedFlagCount?.item_sources?.groups ?? [])
      .filter((source) => !SourcesSkiped.includes(source.name))
      .map((source) => source.name);
    // A source such as twitter can be added more than once on a blueprint.
    return Array.from(new Set(names)).sort();
  }, [listedFlagCount]);
  const postTypesOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (listedFlagCount?.post_types?.groups ?? []).map((post) => post.name)
        )
      ).sort(),
    [listedFlagCount]
  );

  const handleDateChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    name: string
  ) => {
    if (name === "fromDate") setFromDate(e?.target?.value);
    else if (name === "toDate") setToDate(e?.target?.value);
    applyFilterByType(SummaryFilterTypeEnum.date, e, name);
  };
  function requestRerunImpl() {
    reportCancel(report.report_uuid);
    navigateCreatePerson({
      state: { rerun: true, parent_report: report },
    });
  }

  function updateSelection(group: string, key: string, value: boolean) {
    setSelections((prevSelections) => {
      // Group checkboxes call this once per child, so drop every match first to
      // keep repeated checks from duplicating an already selected value. Matching
      // ignores case because the same option can arrive title-cased or lowercased.
      const remaining = prevSelections[group].filter(
        (k) => k.toLowerCase() !== key.toLowerCase()
      );
      return {
        ...prevSelections,
        [group]: value ? [...remaining, key] : remaining,
      };
    });
  }

  function requestRerun() {
    show(
      "Rerun Report?",
      "",
      (action: ConfirmDialogActions) => {
        if (action === "continue") {
          requestRerunImpl();
          hide();
        }
      },
      <div>
        <p className="p-2">
          In order to correct person information, we will need to rerun this
          report from the start.
        </p>
        <p className="p-2">
          By confirming to rerun, this report will be cancelled and a new one
          created using the new person information you are about to enter. This
          will result in a new report charge as well. Do you wish to cancel the
          current report and request a new version?
        </p>
      </div>
    );
  }

  return (
    <div className="mb-3">
      {modal}
      <div className="flex flex-nowrap flex-col">
        <div className="bg-gray-50 rounded-md h-auto border-slate-900 p-4 min-w-0">
          <div className="mb-2 min-w-0">
            <span className="mr-1">Check Type:</span>
            <span
              className={classNames(
                `font-semibold`,
                checkTypeColor,
                LONG_TEXT_WRAP_CLASSES
              )}
            >
              {report.blueprint_name}
            </span>
          </div>
          <div className="mb-2">
            <span className="mr-1">Behavior Kit:</span>
            <span className={`font-semibold`}>
              {report?.behavior_kit_name ?? "General"}
            </span>
          </div>
          <div
            className={classNames(
              "text-xl leading-7 font-bold mb-2",
              LONG_TEXT_WRAP_CLASSES
            )}
          >
            {report.full_name}
            <EuRegionBadge personUuid={report.person_uuid} className="ml-2" />
          </div>
          <div className="mb-2 min-w-0">
            <span className="mr-1">Company:</span>
            <span
              className={classNames("font-semibold", LONG_TEXT_WRAP_CLASSES)}
            >
              {report.parent_company_name ?? report.company_name ?? "N/A"}
            </span>
          </div>
          <div className="mb-2 min-w-0">
            <span className="mr-1">Report ID:</span>
            <span
              className={classNames("font-semibold", LONG_TEXT_WRAP_CLASSES)}
            >
              {report.report_uuid}
            </span>
          </div>
          <ViewReportLogsButton
            onClick={showReportLog}
            canAddNotes={canAddNotes}
          />
        </div>
        <div className="p-4">
          <div className="text-lg leading-5 font-semibold mb-4 flex items-start justify-between gap-2 min-w-0">
            <span
              className={classNames(
                "flex-1 min-w-0 pr-2",
                LONG_TEXT_WRAP_CLASSES
              )}
            >
              Person Info:
            </span>
            {reportDetailType === ReportDetailTypes.PROFILE_QA && (
              <Button
                text="Edit"
                colorType={ButtonColorTypes.cancel}
                onClick={requestRerun}
                className="shrink-0"
              />
            )}
          </div>
          <PersonInfoList report={report} />
        </div>
        {errorBlueprint && (
          <ErrorMessageContainer message="Error loading check type" />
        )}
        {(flagsLoading || baselineLoading) && <LoadingSpinner />}
        {subStep === "posts" && !flagsLoading && !baselineLoading && (
          <div>
            <div className="text-lg bg-gray-50 leading-5 font-semibold p-4 mb-4 rounded-md">
              Filters:
            </div>
            <div className="flex flex-nowrap flex-col py-2">
              <div
                className="flex justify-between "
                onClick={() => {
                  setShowSourceFilter(!showSourceFilter);
                }}
              >
                <div className="text-md font-semibold mb-2 ml-4">Source:</div>
                <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                  {showSourceFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </div>
              </div>

              <div
                className={classNames(
                  "flex flex-nowarp flex-col",
                  showSourceFilter ? "" : "hidden"
                )}
              >
                <div>
                  {itemSourcesOptions.map((source: string, idx: number) => {
                      const counts = displayedCount(
                        countByName(
                          listedFlagCount?.item_sources.groups,
                          source
                        ) ?? 0,
                        countByName(flagCount?.item_sources.groups, source),
                        showFlagTotals
                      );
                      return (
                        <div
                          key={`source-${source}-${idx}`}
                          className="flex gap-1 text-sm ml-6 mb-1"
                        >
                          <LabeledCheckbox
                            key={source + idx}
                            name={source}
                            onChange={(e) => {
                              applyFilterByType(
                                SummaryFilterTypeEnum.source,
                                e,
                                source
                              );
                              updateSelection(
                                "source",
                                source,
                                e.target.checked
                              );
                            }}
                            checked={selections.source.includes(source)}
                          ></LabeledCheckbox>
                          {source}{" "}
                          <FilterCount
                            current={counts.current}
                            total={counts.total}
                            showTotal={counts.showTotal}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            {!hideItemTypeFilter && (
              <div className="flex flex-nowrap flex-col py-2">
                <div
                  className="flex justify-between "
                  onClick={() => {
                    setShowItemTypeFilter(!showItemTypeFilter);
                  }}
                >
                  <div className="text-md font-semibold mb-2 ml-4">
                    Item Type:
                  </div>
                  <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                    {showItemTypeFilter ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )}
                  </div>
                </div>

                <div
                  className={classNames(
                    "flex flex-nowarp flex-col",
                    showItemTypeFilter ? "" : "hidden"
                  )}
                >
                  <div>
                    {itemTypesOptions.map((itemType: string, idx: number) => {
                        const internalName = Object.keys(allowedItemTypes).find(
                          (key) => allowedItemTypes[key] === itemType
                        );
                        const counts = displayedCount(
                          countByName(
                            listedFlagCount?.item_types.groups,
                            internalName ?? ""
                          ) ?? 0,
                          countByName(
                            flagCount?.item_types.groups,
                            internalName ?? ""
                          ),
                          showFlagTotals
                        );
                        return (
                          <div
                            key={`itemType-${itemType}-${idx}`}
                            className="flex gap-1 text-sm ml-6 mb-1"
                          >
                            <LabeledCheckbox
                              key={itemType + idx}
                              name={itemType}
                              onChange={(e) => {
                                applyFilterByType(
                                  SummaryFilterTypeEnum.itemType,
                                  e,
                                  internalName ?? ""
                                );
                                updateSelection(
                                  "itemType",
                                  internalName ?? "",
                                  e.target.checked
                                );
                              }}
                              checked={selections["itemType"].includes(
                                internalName ?? ""
                              )}
                            ></LabeledCheckbox>
                            {itemType}{" "}
                            <FilterCount
                              current={counts.current}
                              total={counts.total}
                              showTotal={counts.showTotal}
                            />
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-nowrap flex-col py-2">
              <div
                className="flex justify-between "
                onClick={() => {
                  setShowPostTypeFilter(!showPostTypeFilter);
                }}
              >
                <div className="text-md font-semibold mb-2 ml-4">
                  Post Type:
                </div>
                <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                  {showPostTypeFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </div>
              </div>

              <div
                className={classNames(
                  "flex flex-nowarp flex-col ml-6",
                  showPostTypeFilter ? "" : "hidden"
                )}
              >
                <div>
                  {postTypesOptions.map((source: string, idx: number) => {
                      const counts = displayedCount(
                        countByName(listedFlagCount?.post_types.groups, source) ??
                          0,
                        countByName(flagCount?.post_types.groups, source),
                        showFlagTotals
                      );
                      return (
                        <div
                          key={`postType-${source}-${idx}`}
                          className="flex text-sm gap-1 mb-1"
                        >
                          <LabeledCheckbox
                            key={source + idx}
                            name={source}
                            onChange={(e) => {
                              applyFilterByType(
                                SummaryFilterTypeEnum.postType,
                                e,
                                source.toLowerCase()
                              );
                              updateSelection(
                                "postType",
                                source.toLowerCase(),
                                e.target.checked
                              );
                            }}
                            checked={selections["postType"].includes(
                              source.toLowerCase()
                            )}
                          ></LabeledCheckbox>
                          {source}{" "}
                          <FilterCount
                            current={counts.current}
                            total={counts.total}
                            showTotal={counts.showTotal}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className="flex flex-nowrap flex-col py-2">
              <div
                className="flex justify-between "
                onClick={() => {
                  setShowBehaviorsFilter(!showBehaviorsFilter);
                }}
              >
                <div className="text-md font-semibold mb-2 ml-4">
                  Behaviors:
                </div>
                <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                  {showBehaviorsFilter ? (
                    <ChevronUpIcon />
                  ) : (
                    <ChevronDownIcon />
                  )}
                </div>
              </div>

              <div
                className={classNames(
                  "flex flex-nowarp flex-col ml-6",
                  showBehaviorsFilter ? "" : "hidden"
                )}
              >
                <SidebarBehaviors
                  flagCount={listedFlagCount}
                  filteredFlagCount={flagCount}
                  filtersActive={
                    !isFilterEmpty &&
                    filteredCountsReady !== false &&
                    !!baselineFlagCount
                  }
                  applyFilterByType={applyFilterByType}
                  hasCheckbox={true}
                  onSelectionChange={(key, value) =>
                    updateSelection("behavior", key, value)
                  }
                  selections={selections["behavior"]}
                />
              </div>
            </div>

            <div className="flex flex-nowrap flex-col py-2">
              <div
                className="flex justify-between "
                onClick={() => {
                  setShowKeywordFilter(!showKeywordFilter);
                }}
              >
                <div className="text-md font-semibold mb-2 ml-4">Keywords:</div>
                <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                  {showKeywordFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </div>
              </div>
              <div
                className={classNames(
                  "flex flex-nowarp flex-col ml-6",
                  showKeywordFilter ? "" : "hidden"
                )}
              >
                <SidebarKeywords
                  keywordCount={listedKeywordCount}
                  filteredKeywordCount={keywordCount}
                  filtersActive={
                    !isFilterEmpty &&
                    filteredCountsReady !== false &&
                    !!baselineKeywordCount
                  }
                  keywordGroups={keywordGroups}
                  applyFilterByType={applyFilterByType}
                  hasCheckbox={true}
                  onSelectionChange={(key, value) =>
                    updateSelection("keyword", key, value)
                  }
                  selections={selections["keyword"]}
                />
              </div>
            </div>

            {(listedTopicCount?.groups?.length ?? 0) > 0 && (
              <div className="flex flex-nowrap flex-col py-2">
                <div
                  className="flex justify-between"
                  onClick={() => setShowTopicFilter(!showTopicFilter)}
                >
                  <div className="text-md font-semibold mb-2 ml-4">Topics:</div>
                  <div className="w-5 mr-4 py-1 text-gray-400">
                    {showTopicFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  </div>
                </div>
                <div
                  className={classNames(
                    "flex flex-nowrap flex-col ml-6",
                    showTopicFilter ? "" : "hidden"
                  )}
                >
                  <SidebarTopics
                    topicCount={listedTopicCount}
                    filteredTopicCount={topicCount}
                    filtersActive={
                      !isFilterEmpty &&
                      filteredCountsReady !== false &&
                      !!baselineTopicCount
                    }
                    selections={selections.topic}
                    applyFilterByType={(type, event, source) => {
                      applyFilterByType(type, event, source);
                      updateSelection("topic", source, event.target.checked);
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex flex-nowrap flex-col py-2">
              <div
                className="flex justify-between"
                onClick={() => {
                  setShowDateFilter(!showDateFilter);
                }}
              >
                <div className="text-md font-semibold mb-2 ml-4">
                  Date Range:
                </div>
                <div className="w-5 mr-4 py-1 ali text-gray-400 ">
                  {showDateFilter ? <ChevronUpIcon /> : <ChevronDownIcon />}
                </div>
              </div>
              {showDateFilter && (
                <div className="flex flex-nowrap flex-col px-4">
                  <div className="flex flex-row mb-2">
                    <InputField
                      handleChange={(e) => {
                        handleDateChange(e, "fromDate");
                      }}
                      name={"from-date"}
                      placeholder=""
                      label={"From Date"}
                      type="date"
                      max={toDate}
                      classes={"w-full"}
                      value={fromDate}
                    />
                  </div>
                  <div className="flex flex-row">
                    <InputField
                      handleChange={(e) => {
                        handleDateChange(e, "toDate");
                      }}
                      name={"to-date"}
                      placeholder=""
                      label={"To Date"}
                      type="date"
                      min={fromDate}
                      classes={"w-full"}
                      value={toDate}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarAuditQAPosts;
